import React, { ReactElement, useEffect, useRef } from 'react'
import { Location, useLocation } from 'react-router'
import Loader from '../../../../React/Common/Components/Loader'
import { getFusionHostOrigin, isFusionFramed } from '../../../../NonReact/common/FusionThemeBridge'
import { requestJwtFromParent } from '../../../../NonReact/common/FusionJwtBridge'
import { consumeFusionSignOutRequested } from '../../../../NonReact/common/FusionSignOutIntent'

// Kept in sync with akixi-frontend ccsMessages.ts (FUSION_LOGOUT_MESSAGE_TYPE) and the host listener
// in LegacyIframe.tsx. Posting it makes the host drop to /login when recovery cannot proceed.
const FUSION_LOGOUT_MESSAGE_TYPE = 'akixi:fusion:logout'

// React app is mounted under this basename (createBrowserRouter in Router.tsx); the auth route is
// the segment GenericGuard redirects to. Used to build the full-page return URL and to avoid looping.
const REACT_APP_BASENAME = '/CCS/App/v2'
const AUTH_ROUTE_SEGMENT = '/auth'

// One-shot guard against redirect loops: bounded recovery attempts persisted across the full-page
// reload, cleared once recovery succeeds.
const RECOVERY_ATTEMPTS_KEY = 'akixiFusionAuthRecoveryAttempts'
const MAX_RECOVERY_ATTEMPTS = 2

const postLogoutToParent = (): void => {
    const hostOrigin: Nullable<string> = getFusionHostOrigin()

    if (!isFusionFramed() || !hostOrigin) return

    try {
        window.parent.postMessage({ type: FUSION_LOGOUT_MESSAGE_TYPE }, hostOrigin)
    } catch {
        // Best effort — the parent may be navigating or torn down.
    }
}

const readRecoveryAttempts = (): number => {
    try {
        return Number(window.sessionStorage.getItem(RECOVERY_ATTEMPTS_KEY) ?? '0') || 0
    } catch {
        return 0
    }
}

const writeRecoveryAttempts = (attempts: number): void => {
    try {
        window.sessionStorage.setItem(RECOVERY_ATTEMPTS_KEY, String(attempts))
    } catch {
        // sessionStorage unavailable — loop guard degrades gracefully.
    }
}

const clearRecoveryAttempts = (): void => {
    try {
        window.sessionStorage.removeItem(RECOVERY_ATTEMPTS_KEY)
    } catch {
        // sessionStorage unavailable.
    }
}

/**
 * Builds the full-page URL to return to after recovery. Prefers the page the user was on, captured
 * by {@code GenericGuard} in {@code location.state.from}; falls back to the React app root. Never
 * returns the /auth route, which would loop.
 *
 * @param from - The location the guard redirected away from, if any.
 * @return An absolute URL within the embedded CCS app to reload.
 */
const buildReturnUrl = (from: Nullable<Location>): string => {
    const pathname: string = from?.pathname && !from.pathname.includes(AUTH_ROUTE_SEGMENT) ? from.pathname : ''
    const search: string = from?.search ?? ''

    return `${window.location.origin}${REACT_APP_BASENAME}${pathname}${search}`
}

/**
 * Re-establishes the CCS web session from the host's JWT via the existing fusion-sso endpoint.
 * Same-origin from the iframe's perspective; cross-origin deployments rely on the bypass cookie the
 * security filter persisted on the initial iframe load to patch the new JSESSIONID to
 * SameSite=None;Partitioned (CXUI-583).
 *
 * @param token - The facade JWT returned by the host.
 * @return {@code true} when the session was re-established.
 */
const reestablishSession = async (token: string): Promise<boolean> => {
    try {
        const response: Response = await fetch(`${window.location.origin}/CCS/API/v1/auth/fusion-sso`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            credentials: 'include'
        })

        return response.ok
    } catch {
        return false
    }
}

/**
 * Shown in place of the login form when the embedded CCS iframe lands on /auth because the CCS
 * session dropped while the host's JWT is still valid (CXUI-583). Renders a spinner, asks the host
 * for the current JWT, re-establishes the CCS session with it, and returns the user to the page they
 * were on. If the JWT is genuinely expired (or recovery keeps failing) the host is asked to
 * re-authenticate instead of looping.
 *
 * A deliberate sign-out also lands on /auth, and must not be recovered from; the host is asked to
 * log out instead.
 */
export const FusionAuthRecovery = (): ReactElement => {
    const location = useLocation()
    const started = useRef<boolean>(false)

    useEffect((): void => {
        if (started.current) return

        started.current = true

        const recover = async (): Promise<void> => {
            // A sign-out lands here too, and recovering from it would sign the user straight back in.
            if (consumeFusionSignOutRequested()) {
                clearRecoveryAttempts()
                postLogoutToParent()

                return
            }

            if (readRecoveryAttempts() >= MAX_RECOVERY_ATTEMPTS) {
                postLogoutToParent()

                return
            }

            writeRecoveryAttempts(readRecoveryAttempts() + 1)

            const token: OnlyNullable<string> = await requestJwtFromParent()

            if (!token) {
                postLogoutToParent()

                return
            }

            const reestablished: boolean = await reestablishSession(token)

            if (!reestablished) {
                postLogoutToParent()

                return
            }

            clearRecoveryAttempts()

            const from: Nullable<Location> = (location.state as Nullable<{ from?: Location }>)?.from ?? null

            window.location.replace(buildReturnUrl(from))
        }

        recover().catch((): void => postLogoutToParent())
    }, [location.state])

    return (
        <div data-fusion-auth-spinner className='router-loader-container'>
            <Loader />
        </div>
    )
}
